import { useCallback, useEffect, useMemo, useState } from "react";

import { css } from "@emotion/react";
import {
  DragDropContext,
  Droppable,
  Draggable,
  OnDragEndResponder,
} from "@hello-pangea/dnd";
import BoxContainer from "@hl/base-components/lib/BoxContainer";
import { STATUS_COLOR, TEXT_COLOR } from "@hl/base-components/lib/theme/colors";
import { logError } from "@hl/shared-features/lib/services/logger";
import {
  AspectRatio,
  Box,
  Button,
  Center,
  Group,
  Image,
  Stack,
  Text,
  createStyles,
} from "@mantine/core";
import { Dropzone, FileWithPath, MIME_TYPES } from "@mantine/dropzone";
import { IconUpload, IconX } from "@tabler/icons";
import { isEmpty } from "lodash";
import { FileRejection } from "react-dropzone";

import {
  SupportedMediaType,
  SupportedMediaMime,
} from "apollo/graphql.generated";
import { ReactComponent as IconPhoto } from "assets/icons/photo.svg";
import { mimeFromType, sizeMbByType, typeFromMime } from "utils/media/file";

import { useGetUploadUrlForContractUriMutation } from "../../../tokens/queries/hosted-media.graphql.generated";

import { uploadToS3 } from "./CollectionMediaUploader";

export interface MediaFile {
  url: string;
  type: string;
  name: string;
}

export interface Props {
  onChange: (files: MediaFile[]) => void;

  mediaError: string;
  mediaFiles: { url: string; type: string; name: string }[];
  supportedTypes: SupportedMediaType[];
}

export const UPLOAD_ERROR =
  "Could not upload media! Refresh and try again, or contact support!";

const useStyles = createStyles((theme) => ({
  listItem: {
    paddingBottom: 10,
    borderBottom: `1px solid ${theme.colors.divider[0]}`,
    backgroundColor: theme.colors.baseBackground[0],
  },
}));

const MintPreviewMediaUploader = ({
  mediaError: errorFromProps,
  onChange,
  mediaFiles,
  supportedTypes,
}: Props) => {
  const [media, setMedia] = useState<FileWithPath | null>(null);
  const [mediaError, setMediaError] = useState<string>(errorFromProps);
  const [isMediaUploading, setIsMediaUploading] = useState<boolean>(false);
  const [getUploadUrl] = useGetUploadUrlForContractUriMutation();
  const [rejections, setRejections] = useState<FileRejection[]>([]);

  const onMediaAdded = useCallback(
    (mediaFile: MediaFile) => {
      if (mediaFile) {
        onChange(mediaFiles.concat(mediaFile));
      }
    },
    [onChange, mediaFiles]
  );

  const onMediaRemoved = useCallback(
    (index: number) => {
      const files = Array.from(mediaFiles);
      files.splice(index, 1);
      onChange(files);
    },
    [onChange, mediaFiles]
  );
  useEffect(() => {
    setMediaError(errorFromProps);
  }, [errorFromProps]);

  const supportedMimeTypes = useMemo(() => {
    const types = [];
    if (supportedTypes && supportedTypes.includes(SupportedMediaType.IMAGE)) {
      types.push(
        ...[MIME_TYPES.jpeg, MIME_TYPES.png, MIME_TYPES.webp, MIME_TYPES.gif]
      );
    }
    if (supportedTypes && supportedTypes.includes(SupportedMediaType.AUDIO)) {
      types.push(...["audio/wav", "audio/mpeg"]);
    }
    if (supportedTypes && supportedTypes.includes(SupportedMediaType.VIDEO)) {
      types.push(...[MIME_TYPES.gif, "video/webm", MIME_TYPES.mp4]);
    }
    return types;
  }, [supportedTypes]);

  const onFileSelected = (files: FileWithPath[]) => {
    if ((mediaFiles?.length ?? 0) + files.length > MAX_MEDIA_ATTACHMENTS) {
      setMediaError(`Cannot set over ${MAX_MEDIA_ATTACHMENTS} images`);
    } else {
      let error = "";
      for (const file of files) {
        const mediaType = typeFromMime[file.type];
        // type: fileTypeFromExtension(url),
        if (!supportedMimeTypes.includes(file.type)) {
          error = `Invalid file type: ${file.type}`;
          setMediaError(error);
        }

        const maxSizeInMb = sizeMbByType[mediaType];
        if (file.size > maxSizeInMb * 1000000) {
          error = `The file size cannot be greater than ${maxSizeInMb}MB.`;
          setMediaError(error);
        }
      }

      if (isEmpty(error)) {
        setMedia(files[0]);
        setMediaError("");
      }
    }
  };

  const clearAttachment = (index: number) => {
    setMedia(null);
    setMediaError("");
    onMediaRemoved(index);
  };

  const getMediaUrls = async (mime: SupportedMediaMime) => {
    const getUrlResponse = await getUploadUrl({
      variables: { data: { mime } },
    });

    const metadata = getUrlResponse?.data?.getUploadUrlForContractUri?.metadata;
    if (!metadata) {
      throw new Error(
        `[MintPreviewMediaUploader] Unexpected response from getUploadUrlForContractUri: ${JSON.stringify(
          { getUrlResponse }
        )}`
      );
    }

    const { url, publicUrl } = metadata;
    if (!url || !publicUrl) {
      throw new Error(
        `[MintPreviewMediaUploader] Unexpected upload media metadata: ${JSON.stringify(
          { metadata }
        )}`
      );
    }

    return { url, publicUrl };
  };

  /**
   * Upload media files and replaces the local blob URLs with the public CDN ones.
   * @param file file to be uploaded
   */
  const uploadMediaFileToS3 = async (file: File) => {
    try {
      setIsMediaUploading(true);
      const { url, publicUrl } = await getMediaUrls(
        mimeFromType[file.type] as SupportedMediaMime
      );
      await uploadToS3(file, url);
      onMediaAdded({ url: publicUrl, type: file.type, name: file.name });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err) {
      logError({
        err,
        message: "[CollectionMediaUploader] Failed to fetch upload URL",
      });

      setMediaError(UPLOAD_ERROR);
      setMedia(null);
      //onMediaChanged(null);
    } finally {
      setIsMediaUploading(false);
    }
  };

  useEffect(() => {
    if (media) {
      uploadMediaFileToS3(media);
    }
  }, [media]);

  const onDragEnd: OnDragEndResponder = (result) => {
    if (!result.destination) {
      return;
    }

    const items = Array.from(mediaFiles);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Assuming you have a method to update the mediaFiles state
    onChange(items);
  };
  const { classes } = useStyles();

  const mediaUpload = (
    <Box p={0}>
      <Group>
        <Dropzone
          multiple={
            false
          } /* TODO: allow multiple: https://linear.app/highlight-xyz/issue/ENG-5034/allow-multiple-mint-preview-image-uploads-at-once */
          accept={supportedMimeTypes}
          onDrop={async (files) => {
            setRejections([]);
            onFileSelected(files);
          }}
          onReject={(files) => {
            setRejections(files);
          }}
          loading={isMediaUploading}
          sx={(theme) => ({
            borderWidth: "1px",
            borderColor: mediaError
              ? theme.colors.red[4]
              : theme.colors.black[0],
            borderStyle: "solid",
            minWidth: "64px",
            minHeight: "64px",
            maxWidth: "64px",
            maxHeight: "64px",
          })}
          p={0}
        >
          <AspectRatio ratio={1}>
            <Stack
              align="center"
              justify="center"
              spacing={0}
              style={{ pointerEvents: "none" }}
            >
              <Dropzone.Accept>
                <IconUpload size={24} stroke={1.5} />
              </Dropzone.Accept>
              <Dropzone.Reject>
                <IconX size={24} stroke={1.5} />
              </Dropzone.Reject>
              <Dropzone.Idle>
                <IconPhoto
                  css={css`
                    width: 24px;
                    height: 24px;
                  `}
                />
              </Dropzone.Idle>
            </Stack>
          </AspectRatio>
        </Dropzone>

        <Text size="sm" color={TEXT_COLOR.SECONDARY}>
          Upload an image...
        </Text>
      </Group>
      {rejections.map((rejection: FileRejection) => (
        <>
          <Text
            key={rejection.file.name + rejection.file.lastModified}
            mt="sm"
            size="sm"
            color={STATUS_COLOR.ERROR}
          >
            Error for {rejection.file.name}:
          </Text>
          {rejection.errors.map((error) => (
            <Text
              key={error.code}
              mt="sm"
              size="sm"
              color={STATUS_COLOR.ERROR}
              sx={() => ({ overflowWrap: "break-word" })}
            >
              {error.message}
            </Text>
          ))}
        </>
      ))}
      {mediaError && (
        <Center>
          <Text size="sm" color={STATUS_COLOR.ERROR}>
            {mediaError}
          </Text>
        </Center>
      )}
    </Box>
  );

  return (
    <>
      {mediaFiles.length > 1 && (
        <Text size="xs" mb="sm">
          Drag images to re-order
        </Text>
      )}
      <BoxContainer>
        {mediaFiles.length > 0 && (
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="droppable">
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  {mediaFiles.map((file, index) => (
                    <Draggable
                      key={file.url}
                      draggableId={file.url}
                      index={index}
                    >
                      {(provided) => (
                        <Group
                          position="apart"
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className={classes.listItem}
                          my="sm"
                          noWrap
                        >
                          <Group position="left" spacing={4}>
                            {typeFromMime[file.type] ===
                            SupportedMediaType.IMAGE ? (
                              <Image
                                src={file.url}
                                height={"64px"}
                                width={"64px"}
                                fit="contain"
                                imageProps={{
                                  onLoad: () => URL.revokeObjectURL(file.url),
                                }}
                              />
                            ) : (
                              <video controls loop playsInline height="64px">
                                <source src={file.url} />
                              </video>
                            )}

                            <Group position="center" noWrap sx={{ flex: 1 }}>
                              <Text size="sm" ml="sm">
                                {file.name}
                              </Text>
                            </Group>
                          </Group>
                          <Button
                            size={"lg"}
                            type={"submit"}
                            px={12}
                            onClick={() => clearAttachment(index)}
                          >
                            Remove
                          </Button>
                        </Group>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
        {(mediaFiles?.length ?? 0) < MAX_MEDIA_ATTACHMENTS && mediaUpload}
      </BoxContainer>
    </>
  );
};

export const MAX_MEDIA_ATTACHMENTS = 30;

export default MintPreviewMediaUploader;
